import React, { FC, MouseEvent, useState } from 'react';
import { Button, Grid, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';

import { UpdateGroupInput } from 'api';
import { Dimensions, PaddedPaper, ValidatedSelect, ValidatedTextField } from 'components';
import { useGroupData } from 'features/groups/hooks';

import ImagePicker from './ImagePicker';
import ImageChange from './ImageChange';
import Image from './Image';
import ColorPicker from './ColorPicker';
import { getGroupHexColor } from '../../helpers';

const useStyles = makeStyles(theme => ({
  colorButton: {
    borderRadius: 4,
    borderColor: 'transparent',
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    '&:hover': {
      opacity: 0.9,
      borderColor: 'transparent',
    },
  },
  paper: {
    padding: theme.spacing(4, 4, 5, 3),
  },
  text: {
    color: theme.palette.grey[500],
  },
}));

const GroupDetails: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { values } = useFormikContext<UpdateGroupInput>();
  const { clientDepartments, clientLocations, groupColors } = useGroupData();

  const hexColor = getGroupHexColor(groupColors, values.color);

  const [isImagePickerOpen, setIsImagePickOpen] = useState(false);
  const [colorAnchorEl, setColorAnchorEl] = useState(null);

  function handleImageToggle() {
    setIsImagePickOpen(!isImagePickerOpen);
  }

  function handleColorClick(event: MouseEvent<HTMLButtonElement>) {
    setColorAnchorEl(event.currentTarget);
  }

  function handleColorClose() {
    setColorAnchorEl(null);
  }

  return (
    <PaddedPaper style={{ borderTop: `3px solid ${hexColor}` }} square className={classes.paper}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={3}>
          <Typography variant="subtitle2">{t('groups:groupDetails')}</Typography>
        </Grid>

        <Grid item xs={12} sm={7}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ValidatedTextField
                autoFocus={!values.title}
                persistLabel
                TextFieldProps={{ margin: 'none' }}
                name="title"
                i18nKey="groups:name"
              />
            </Grid>

            <Grid item xs={12}>
              <ValidatedSelect
                persistLabel
                margin="normal"
                name="department_id"
                i18nKey="user:department"
                placeholder={t('common:select')}
                items={clientDepartments?.client_departments}
              />
            </Grid>

            <Grid item xs={12}>
              <ValidatedSelect
                persistLabel
                margin="normal"
                name="location_id"
                i18nKey="user:location"
                placeholder={t('common:select')}
                items={clientLocations?.client_locations}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sm={2}
          style={{ paddingLeft: '28px' }}
          container
          justify="space-between"
          direction="column"
        >
          <Grid item>
            <Typography variant="caption" className={classes.text}>
              {t('groups:image')}
            </Typography>
            <Dimensions>
              {({ width }) => (
                <ImageChange onToggle={handleImageToggle}>
                  <Image size={width} />
                </ImageChange>
              )}
            </Dimensions>

            <ImagePicker open={isImagePickerOpen} onClose={handleImageToggle} />
          </Grid>

          <Grid item>
            <Typography variant="caption" className={classes.text}>
              {t('groups:color')}
            </Typography>
            <Button
              className={classes.colorButton}
              variant="contained"
              style={{ backgroundColor: hexColor }}
              fullWidth
              size="large"
              onClick={handleColorClick}
            >
              {t('groups:changeColor')}
            </Button>
            <ColorPicker anchorEl={colorAnchorEl} onClose={handleColorClose} />
          </Grid>
        </Grid>
      </Grid>
    </PaddedPaper>
  );
};

export default GroupDetails;
